// client/src/utils/returnSummaryParser.ts
export type ReturnSummaryHeader = {
  ClientID: string;
  TaxYear: string;
  ReturnType: string;
  EINorSSN: string;
  OfficeName: string;
  BusinessUnitName: string;
  TaxpayerName: string;
};

export type ReturnSummaryRow = {
  jurisdiction: string;
  details: Record<string, string>;
  _originalIndex?: number;
};

export type ParsedReturnSummary = {
  header: ReturnSummaryHeader;
  returns: ReturnSummaryRow[];
};

function textTrim(s: unknown): string {
  return (s ?? '').toString().replace(/\s+/g, ' ').trim();
}

function buildIndexMap(row: Element): Map<number, string> {
  const m = new Map<number, string>();
  const vals = Array.from(row.querySelectorAll('RowValue'));
  for (const rv of vals) {
    const idx = parseInt(rv.getAttribute('FieldIndex') || '', 10);
    if (!Number.isFinite(idx)) continue;
    m.set(idx, rv.getAttribute('Value') || '');
  }
  return m;
}

function getFieldDataByLocation(view: Element): Map<string, string> {
  const m = new Map<string, string>();
  for (const fd of Array.from(view.querySelectorAll('FieldData'))) {
    // 1040 schema uses Location="IRTNRCP2.X"
    const loc = textTrim(fd.getAttribute('Location'));
    if (!loc) continue;
    m.set(loc, textTrim(fd.getAttribute('Value')));
  }
  return m;
}

function pickJurisdictionNamesFromPrefix(view: Element, prefix: string, startN: number, colCount: number): string[] {
  const byLoc = getFieldDataByLocation(view);

  const candidates = Array.from(byLoc.entries())
    .map(([loc, val]) => {
      const m = new RegExp(`^${prefix}\\.(\\d+)$`).exec(loc);
      const n = m ? parseInt(m[1], 10) : NaN;
      return { n, val: textTrim(val) };
    })
    .filter((x) => Number.isFinite(x.n) && x.n >= startN && x.val.length > 0)
    .sort((a, b) => a.n - b.n)
    .slice(0, colCount)
    .map((x) => x.val);

  if (candidates.length === colCount) return candidates;
  return Array.from({ length: colCount }, (_, i) => `Jurisdiction ${i + 1}`);
}

function parseCorpContinuationGrid(rows: Element[]): ReturnSummaryRow[] {
  if (!rows.length) return [];

  // Build label map from the first row that actually contains labels (usually Federal)
  const labelRow = rows.find((r) => {
    const m = buildIndexMap(r);
    for (const idx of m.keys()) {
      if (idx >= 3 && idx % 2 === 1 && textTrim(m.get(idx))) return true;
    }
    return false;
  });

  const labelMap = new Map<number, string>(); // odd FieldIndex -> label
  if (labelRow) {
    const m = buildIndexMap(labelRow);
    const indices = Array.from(m.keys()).sort((a, b) => a - b);
    for (const idx of indices) {
      if (idx >= 3 && idx % 2 === 1) {
        const label = textTrim(m.get(idx));
        if (label) labelMap.set(idx, label);
      }
    }
  }

  const returns: ReturnSummaryRow[] = [];

  for (const row of rows) {
    const rmap = buildIndexMap(row);
    if (!rmap.size) continue;

    const jurisdictionBase = textTrim(rmap.get(2) || '');
    if (!jurisdictionBase) continue;

    const details: Record<string, string> = {};

    // Use labelMap if we have it (this fixes rows like Alabama that only include values)
    if (labelMap.size) {
      for (const [oddIdx, label] of labelMap.entries()) {
        const value = textTrim(rmap.get(oddIdx + 1) || '');
        // Keep even if blank so UI shows the key (optional); otherwise guard with `if (value)`
        details[label] = value;
      }
    } else {
      // Fallback: if no label map, parse label/value pairs that exist in this row
      const indices = Array.from(rmap.keys()).sort((a, b) => a - b);
      for (const idx of indices) {
        if (idx < 3) continue;
        if (idx % 2 === 1) {
          const label = textTrim(rmap.get(idx));
          if (!label) continue;
          const value = textTrim(rmap.get(idx + 1) || '');
          details[label] = value;
        }
      }
    }

    // Make duplicates readable (Alabama (20C) vs Alabama (CPT))
    const form = textTrim(details['Form Name'] || '');
    const jurisdiction = form ? `${jurisdictionBase} (${form})` : jurisdictionBase;

    returns.push({ jurisdiction, details });
  }

  return returns;
}

function parseRowPerJurisdictionWithSharedLabels(rows: Element[], jurisdictionFieldIndex: number): ReturnSummaryRow[] {
  if (!rows.length) return [];

  // Find a row that actually contains label cells (odd indices >= 2 in this format)
  const labelRow = rows.find((r) => {
    const m = buildIndexMap(r);
    for (const idx of m.keys()) {
      if (idx >= jurisdictionFieldIndex + 1 && idx % 2 === 0 && textTrim(m.get(idx))) return true;
    }
    return false;
  });

  // Build label map: even FieldIndex -> label (because labels are at 2,4,6... for this format)
  const labelMap = new Map<number, string>();
  if (labelRow) {
    const m = buildIndexMap(labelRow);
    const indices = Array.from(m.keys()).sort((a, b) => a - b);
    for (const idx of indices) {
      if (idx > jurisdictionFieldIndex && idx % 2 === 0) {
        const label = textTrim(m.get(idx));
        if (label) labelMap.set(idx, label);
      }
    }
  }

  const returns: ReturnSummaryRow[] = [];

  for (const row of rows) {
    const rmap = buildIndexMap(row);
    if (!rmap.size) continue;

    const jurisdiction = textTrim(rmap.get(jurisdictionFieldIndex) || '');
    if (!jurisdiction) continue;

    const details: Record<string, string> = {};

    if (labelMap.size) {
      // Values live in the next index (labelIdx + 1)
      for (const [labelIdx, label] of labelMap.entries()) {
        const value = textTrim(rmap.get(labelIdx + 1) || '');
        details[label] = value;
      }
    } else {
      // Fallback: parse label/value pairs that exist in this row
      const indices = Array.from(rmap.keys()).sort((a, b) => a - b);
      for (const idx of indices) {
        if (idx <= jurisdictionFieldIndex) continue;
        if (idx % 2 === 0) {
          const label = textTrim(rmap.get(idx));
          if (!label) continue;
          const value = textTrim(rmap.get(idx + 1) || '');
          details[label] = value;
        }
      }
    }

    returns.push({ jurisdiction, details });
  }

  return returns;
}

function parseMatrixGrid(view: Element, grid: Element, rows: Element[]): ReturnSummaryRow[] {
  const fieldHeaders = Array.from(grid.querySelectorAll('FieldHeader'));
  const colCount = Math.max(0, fieldHeaders.length - 1); // minus label column
  if (colCount <= 0) return [];

  // Determine how many data columns are actually populated in rows.
  // Handles the per-entity 1040 schema where CCH emits one View per jurisdiction
  // with 3 FieldHeaders but only FieldIndex 1 (label) + FieldIndex 2 (value) in rows.
  let effectiveColCount = 0;
  for (const row of rows) {
    const m = buildIndexMap(row);
    for (const [idx, val] of m.entries()) {
      // Only count columns that actually carry data — empty FieldIndex entries
      // (e.g. Extended Due Date rows in per-entity Views) must not inflate the count.
      if (idx >= 2 && textTrim(val)) effectiveColCount = Math.max(effectiveColCount, idx - 1);
    }
  }
  effectiveColCount = Math.min(Math.max(effectiveColCount, 1), colCount);

  const hierarchy = view.querySelector('Identifier')?.getAttribute('Hierarchy') || '';

  // 1040 (I)
  let jurisdictionNames: string[] | null = null;
  if (hierarchy.includes('Federal\\Return Summary\\Return Summary, Page 2')) {
    jurisdictionNames = pickJurisdictionNamesFromPrefix(view, 'IRTNRCP2', 3, effectiveColCount);

    // Per-entity schema fallback: when CCH splits each jurisdiction into its own View,
    // state Views (Entity ID > 1) store the state name in IRTNRCP2.2 rather than IRTNRCP2.3+.
    if (effectiveColCount === 1 && jurisdictionNames.every((j, i) => j === `Jurisdiction ${i + 1}`)) {
      const byLoc = getFieldDataByLocation(view);
      const stateName = textTrim(byLoc.get('IRTNRCP2.2') ?? '');
      if (stateName) jurisdictionNames = [stateName];
    }
  }

  // Fallback
  if (!jurisdictionNames) {
    jurisdictionNames = Array.from({ length: effectiveColCount }, (_, i) => `Jurisdiction ${i + 1}`);
  }

  jurisdictionNames = jurisdictionNames.map((s) => textTrim(s));

  const returns: ReturnSummaryRow[] = jurisdictionNames.map((j, i) => ({
    jurisdiction: j || `Jurisdiction ${i + 1}`,
    details: {},
    _originalIndex: i,
  }));

  for (const row of rows) {
    const byIndex = buildIndexMap(row);
    if (!byIndex.size) continue;

    const label = textTrim(byIndex.get(1));
    if (!label) continue;

    for (let c = 0; c < effectiveColCount; c++) {
      const value = textTrim(byIndex.get(c + 2));
      returns[c].details[label] = value;
    }
  }

  return returns;
}

/**
 * Detect whether this GridData is a "matrix" (1040 style) vs the older "odd/even label/value" layout.
 *
 * Matrix characteristics:
 * - Row labels are in FieldIndex=1
 * - Multiple columns exist at FieldIndex=2..N
 * - First row does NOT contain additional odd-index labels (3,5,7...) the way the old format does
 */
function isMatrixGrid(view: Element, grid: Element, rows: Element[]): boolean {
  if (!rows.length) return false;

  const hierarchy = view.querySelector('Identifier')?.getAttribute('Hierarchy') || '';

  const is1040Page2 = hierarchy.includes('Federal\\Return Summary\\Return Summary, Page 2');
  const isCorpSummary = hierarchy.includes('Federal\\Corporation Return Summary');

  if (!is1040Page2 && !isCorpSummary) return false;

  // Old odd/even schema guard
  const firstMap = buildIndexMap(rows[0]);
  const firstCol = textTrim(firstMap.get(1));
  if (firstCol.toLowerCase() === 'jurisdiction') return false;

  // Must have a <Fields> block with contiguous FieldIndex 1..N
  const fieldsEl = grid.querySelector('Fields');
  if (!fieldsEl) return false;

  const headers = Array.from(fieldsEl.querySelectorAll('FieldHeader'));
  if (headers.length < 3) return false;

  const idxs = headers
    .map((h) => parseInt(h.getAttribute('FieldIndex') || '', 10))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);

  const contiguous =
    idxs.length === headers.length &&
    idxs[0] === 1 &&
    idxs[idxs.length - 1] === idxs.length;

  return contiguous;
}

function parseOddEvenGrid(_grid: Element, rows: Element[]): ReturnSummaryRow[] {
  // Map odd FieldIndex -> label using first row.
  const firstRowValues = Array.from(rows[0].querySelectorAll('RowValue'));
  const firstMap = new Map<number, string>();
  for (const rv of firstRowValues) {
    const idx = parseInt(rv.getAttribute('FieldIndex') || '', 10);
    if (!Number.isFinite(idx)) continue;
    firstMap.set(idx, rv.getAttribute('Value') || '');
  }
  if (!firstMap.size) return [];

  const maxIdx = Math.max(...Array.from(firstMap.keys()));
  const labelMap = new Map<number, string>();
  for (let i = 1; i <= maxIdx; i += 2) {
    let label = textTrim(firstMap.get(i));
    if (!label) label = i === 1 ? 'Jurisdiction' : '';
    labelMap.set(i, label);
  }

  const returns: ReturnSummaryRow[] = [];

  for (const row of rows) {
    const rmap = new Map<number, string>();
    for (const rv of Array.from(row.querySelectorAll('RowValue'))) {
      const idx = parseInt(rv.getAttribute('FieldIndex') || '', 10);
      if (!Number.isFinite(idx)) continue;
      rmap.set(idx, rv.getAttribute('Value') || '');
    }
    if (!rmap.size) continue;

    const jurisdiction = textTrim(rmap.get(2) || rmap.get(1) || '');
    if (!jurisdiction) continue;

    const details: Record<string, string> = {};
    for (const [oddIdx, label] of labelMap.entries()) {
      if (!label || label === 'Jurisdiction') continue;
      const v = textTrim(rmap.get(oddIdx + 1) || '');
      details[label] = v;
    }

    returns.push({ jurisdiction, details });
  }

  return returns;
}

export function parseReturnSummaryXml(xmlText: string): ParsedReturnSummary {
  const cleaned = (xmlText || '').replace(/^\uFEFF/, '').trimStart();
  const parser = new DOMParser();
  const doc = parser.parseFromString(cleaned, 'application/xml');

  const parseErr = doc.querySelector('parsererror');
  if (parseErr) {
    const preview = cleaned.slice(0, 160).replace(/\s+/g, ' ');
    throw new Error(
      `XML parse error: ${(parseErr.textContent || '').slice(0, 200)} | Response starts with: ${preview}`
    );
  }

  const taxReturn = doc.querySelector('Payload > TaxReturn');
  if (!taxReturn) {
    throw new Error('Unexpected XML structure: missing Payload > TaxReturn');
  }

  const rh = taxReturn.querySelector('ReturnHeader');
  const tp = taxReturn.querySelector('TaxPayerDetails');

  const header: ReturnSummaryHeader = {
    ClientID: rh?.getAttribute('ClientID') || '',
    TaxYear: rh?.getAttribute('TaxYear') || '',
    ReturnType: rh?.getAttribute('ReturnType') || '',
    EINorSSN: rh?.getAttribute('EINorSSN') || '',
    OfficeName: rh?.getAttribute('OfficeName') || '',
    BusinessUnitName: rh?.getAttribute('BusinessUnitName') || '',
    TaxpayerName: tp?.getAttribute('NameLine1') || '',
  };

  const returns: ReturnSummaryRow[] = [];

  const views = Array.from(taxReturn.querySelectorAll('View'));
  for (const view of views) {
    const grid = view.querySelector('GridData');
    if (!grid) continue;

    const rows = Array.from(grid.querySelectorAll('Row'));
    if (!rows.length) continue;

    const hierarchy = view.querySelector('Identifier')?.getAttribute('Hierarchy') || '';

    // Special case: Corporation Return Summary Continuation (row-per-jurisdiction, labels across columns)
    if (hierarchy.includes('Federal\\Corporation Return Summary Continuation')) {
      returns.push(...parseCorpContinuationGrid(rows));
      continue;
    }
    if (hierarchy.includes('Federal\\Miscellaneous\\Return Summary, Page 2')) {
      // F (1041) format: jurisdiction is in FieldIndex=1
      returns.push(...parseRowPerJurisdictionWithSharedLabels(rows, 1));
      continue;
    }
    if (isMatrixGrid(view, grid, rows)) {
      returns.push(...parseMatrixGrid(view, grid, rows));
    } else {
      returns.push(...parseOddEvenGrid(grid, rows));
    }
  }

  // DEBUG: sanity check parser output
if (typeof console !== 'undefined') {
  console.log('[returnSummaryParser] returns=', returns.length, returns.map((r) => r.jurisdiction));
}

  return { header, returns };
}

export function normalizeSearch(s: unknown): string {
  return (s || '').toString().toLowerCase().trim();
}

export function returnToSearchText(r: ReturnSummaryRow): string {
  const bits: string[] = [r.jurisdiction];
  for (const [k, v] of Object.entries(r.details || {})) {
    bits.push(k);
    bits.push(v);
  }
  return normalizeSearch(bits.join(' '));
}